import type { ApiClient } from './client';
import type {
  BiltEvent,
  CheckinRegistrationRow,
  CreateEventPayload,
  CreateEventClassPayload,
  CreateRegistrationPayload,
  EventArchiveData,
  EventClass,
  EventRegistration,
  PublicEventData,
  UpdateEventPayload,
} from '../../types/event';

/**
 * Event API methods (Phase 2 Event MVP).
 */
export const eventsMethods = {
  async createEvent(payload: CreateEventPayload): Promise<BiltEvent> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<BiltEvent>('/events_create.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to create event');
  },

  async getOrganizerEvents(): Promise<BiltEvent[]> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<BiltEvent[]>('/events_list.php', { method: 'GET' });
    if (response.success && Array.isArray(response.data)) return response.data;
    if (response.success) return [];
    throw new Error(response.error || response.message || 'Failed to load events');
  },

  async getEvent(eventId: number): Promise<BiltEvent> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<BiltEvent>(
      `/event_get.php?id=${encodeURIComponent(String(eventId))}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Event not found');
  },

  async getPublicEvents(filters: {
    q?: string;
    city?: string;
    state?: string;
    when?: 'upcoming' | 'past' | 'all';
    page?: number;
    limit?: number;
  } = {}): Promise<{
    data: BiltEvent[];
    pagination: {
      page: number;
      limit: number;
      total_count: number;
      total_pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters.q) params.set('q', filters.q);
    if (filters.city) params.set('city', filters.city);
    if (filters.state) params.set('state', filters.state);
    if (filters.when) params.set('when', filters.when);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    const response = await this.makeRequest<BiltEvent[]>(
      `/events_public_list.php${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    if (response.success && Array.isArray(response.data)) {
      const pagination = (response as { pagination?: {
        page: number;
        limit: number;
        total_count: number;
        total_pages: number;
      } }).pagination;
      return {
        data: response.data,
        pagination: pagination ?? {
          page: filters.page || 1,
          limit: filters.limit || 20,
          total_count: response.data.length,
          total_pages: 1,
        },
      };
    }
    if (response.success) {
      return {
        data: [],
        pagination: { page: 1, limit: 20, total_count: 0, total_pages: 0 },
      };
    }
    throw new Error(response.error || response.message || 'Failed to load events');
  },

  async getPublicEventBySlug(
    query: string | { organizer: string; slug: string } | { legacySlug: string }
  ): Promise<PublicEventData> {
    let qs: string;
    if (typeof query === 'string') {
      qs = `slug=${encodeURIComponent(query)}`;
    } else if ('legacySlug' in query) {
      qs = `slug=${encodeURIComponent(query.legacySlug)}`;
    } else {
      qs = `organizer=${encodeURIComponent(query.organizer)}&slug=${encodeURIComponent(query.slug)}`;
    }
    const response = await this.makeRequest<PublicEventData>(
      `/event_by_slug.php?${qs}`,
      {
        method: 'GET',
        // Optional auth so registrants see private updates when signed in.
        headers: this.isAuthenticated() ? this.getAuthHeaders() : undefined,
      }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Event not found');
  },

  async getEventClasses(eventId: number): Promise<EventClass[]> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventClass[]>(
      `/event_classes_list.php?event_id=${encodeURIComponent(String(eventId))}`,
      { method: 'GET' }
    );
    if (response.success && Array.isArray(response.data)) return response.data;
    if (response.success) return [];
    throw new Error(response.error || response.message || 'Failed to load classes');
  },

  async createEventClass(payload: CreateEventClassPayload): Promise<EventClass> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventClass>('/event_class_create.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to create class');
  },

  async updateEventClass(id: number, updates: { name?: string; description?: string; sort_order?: number }): Promise<EventClass> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventClass>('/event_class_update.php', {
      method: 'POST',
      body: JSON.stringify({ id, ...updates }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to update class');
  },

  async deleteEventClass(id: number): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{ id: number }>('/event_class_delete.php', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    if (!response.success) throw new Error(response.error || response.message || 'Failed to delete class');
  },

  async reorderEventClasses(eventId: number, classIds: number[]): Promise<EventClass[]> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventClass[]>('/event_classes_reorder.php', {
      method: 'POST',
      body: JSON.stringify({ event_id: eventId, class_ids: classIds }),
    });
    if (response.success && Array.isArray(response.data)) return response.data;
    throw new Error(response.error || response.message || 'Failed to reorder classes');
  },

  async submitRegistration(payload: CreateRegistrationPayload): Promise<EventRegistration> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventRegistration>('/registrations_create.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to submit registration');
  },

  async getEventRegistrations(eventId: number): Promise<EventRegistration[]> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventRegistration[]>(
      `/event_registrations_list.php?event_id=${encodeURIComponent(String(eventId))}`,
      { method: 'GET' }
    );
    if (response.success && Array.isArray(response.data)) return response.data;
    if (response.success) return [];
    throw new Error(response.error || response.message || 'Failed to load registrations');
  },

  /** CSV export — uses raw fetch because the response is not JSON. */
  async exportEventRegistrationsCsv(eventId: number): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const url = `${this.baseUrl}/event_registrations_export.php?event_id=${encodeURIComponent(String(eventId))}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      let message = 'Export failed';
      try {
        const data = await response.json();
        message = data.error || data.message || message;
      } catch {
        /* CSV error body may not be JSON */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : `registrations-event-${eventId}.csv`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  },

  async getEventCheckinList(eventId: number, query?: string): Promise<CheckinRegistrationRow[]> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const params = new URLSearchParams({ event_id: String(eventId) });
    if (query) params.set('q', query);
    const response = await this.makeRequest<CheckinRegistrationRow[]>(
      `/event_checkin_list.php?${params.toString()}`,
      { method: 'GET' }
    );
    if (response.success && Array.isArray(response.data)) return response.data;
    if (response.success) return [];
    throw new Error(response.error || response.message || 'Failed to load check-in list');
  },

  async updateRegistrationCheckin(
    registrationId: number,
    status: 'checked_in' | 'no_show' | 'submitted' | 'confirmed'
  ): Promise<EventRegistration> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventRegistration>('/registration_checkin_update.php', {
      method: 'POST',
      body: JSON.stringify({ registration_id: registrationId, status }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to update check-in');
  },

  async getEventArchive(
    query: string | { organizer: string; slug: string } | { legacySlug: string }
  ): Promise<EventArchiveData> {
    let qs: string;
    if (typeof query === 'string') {
      qs = `slug=${encodeURIComponent(query)}`;
    } else if ('legacySlug' in query) {
      qs = `slug=${encodeURIComponent(query.legacySlug)}`;
    } else {
      qs = `organizer=${encodeURIComponent(query.organizer)}&slug=${encodeURIComponent(query.slug)}`;
    }
    const response = await this.makeRequest<EventArchiveData>(
      `/event_archive.php?${qs}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Event archive not found');
  },

  async updateEvent(id: number, updates: UpdateEventPayload): Promise<BiltEvent> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<BiltEvent>('/event_update.php', {
      method: 'POST',
      body: JSON.stringify({ id, ...updates }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to update event');
  },

  async uploadEventFlyer(eventId: number, file: File): Promise<{ flyer_path: string }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const formData = new FormData();
    formData.append('event_id', String(eventId));
    formData.append('flyer', file);
    const response = await this.makeRequest<{ flyer_path: string }>('/event_flyer_upload.php', {
      method: 'POST',
      body: formData,
    });
    if (response.success && response.data?.flyer_path) return response.data;
    throw new Error(response.error || response.message || 'Failed to upload event flyer');
  },

  async deleteEventFlyer(eventId: number): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest('/event_flyer_upload.php', {
      method: 'DELETE',
      body: JSON.stringify({ event_id: eventId }),
    });
    if (!response.success) {
      throw new Error(response.error || response.message || 'Failed to remove event flyer');
    }
  },
} satisfies ThisType<ApiClient>;
